class SafeLoadScripts extends HTMLElement {
  constructor() {
    super();
    this.retryTimer = null;
  }

  connectedCallback() {
    this.loadScriptsWhenReady(0);
  }

  disconnectedCallback() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  loadScriptsWhenReady(attempt) {
    const registry = window.wetheme && window.wetheme.webcomponentRegistry;

    if (!registry) {
      if (attempt < 200) {
        this.retryTimer = setTimeout(() => this.loadScriptsWhenReady(attempt + 1), 25);
      }
      return;
    }

    const scripts = this.querySelectorAll(':scope > script');

    scripts.forEach((script) => {
      const key = script.dataset.flowLoadKey;
      if (!key || registry.checkScriptLoaded(key) || SafeLoadScripts.loadingKeys.has(key)) return;

      SafeLoadScripts.loadingKeys.add(key);

      const postLoadedScript = document.createElement('script');
      const { src, defer, async, type } = script;
      postLoadedScript.src = src;
      postLoadedScript.defer = defer;
      postLoadedScript.async = async;
      postLoadedScript.type = type;
      postLoadedScript.dataset.flowLoadKey = key;

      const clearLoadingKey = () => SafeLoadScripts.loadingKeys.delete(key);
      postLoadedScript.addEventListener('load', clearLoadingKey, { once: true });
      postLoadedScript.addEventListener('error', clearLoadingKey, { once: true });

      if (this.parentElement) {
        this.parentElement.appendChild(postLoadedScript);
      } else {
        clearLoadingKey();
      }
    });
  }
}

SafeLoadScripts.loadingKeys = new Set();

if (!customElements.get('safe-load-scripts')) {
  customElements.define('safe-load-scripts', SafeLoadScripts);
}

/* PP_LEAD_ATTRIBUTION_V2
 * Keeps first-touch campaign data in the browser and forwards it to Beehiiv.
 * A Meta Lead is emitted only after an explicit provider success signal.
 */
(function () {
  var LEAD_PATHS = {
    "/pages/free-circuits-escape-room": true,
    "/pages/free-electrostatics-warmups": true
  };
  var ATTRIBUTION_PATHS = {
    "/pages/free-circuits-escape-room": true,
    "/pages/free-electrostatics-warmups": true,
    "/pages/welcome-offer": true
  };
  var SUCCESS_PATH = "/pages/welcome-offer";
  var BEEHIIV_ORIGIN = "https://subscribe-forms.beehiiv.com";
  var META_PIXEL_ID = "1461056888962684";
  var ATTRIBUTION_KEY = "pp_first_touch_attribution_v1";
  var PENDING_KEY = "pp_lead_pending_v2";
  var PENDING_TTL_MS = 60 * 60 * 1000;
  var TRACKED_KEYS = [
    "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
    "fbclid", "gclid", "ttclid", "msclkid"
  ];
  var inMemoryLeadFired = false;
  var confirmedLead = null;

  function normalizePath(path) {
    if (!path || path === "/") return path || "/";
    return path.replace(/\/+$/, "");
  }

  var currentPath = normalizePath(window.location.pathname);

  function paramsFromSearch() {
    var out = {};
    var params = new URLSearchParams(window.location.search || "");
    TRACKED_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) out[key] = value;
    });
    return out;
  }

  function sourceFromReferrer(referrer) {
    var host = "";
    try { host = new URL(referrer).hostname.toLowerCase(); } catch (e) {}
    if (!host || host === window.location.hostname.toLowerCase()) return "";
    if (host.indexOf("facebook.") !== -1 || host.indexOf("fb.") !== -1 || host.indexOf("l.facebook") !== -1) return "facebook";
    if (host.indexOf("instagram.") !== -1) return "instagram";
    if (host.indexOf("tiktok.") !== -1) return "tiktok";
    if (host.indexOf("youtube.") !== -1 || host.indexOf("youtu.be") !== -1) return "youtube";
    if (host.indexOf("pinterest.") !== -1) return "pinterest";
    if (host.indexOf("teacherspayteachers.") !== -1) return "teacherspayteachers";
    if (host.indexOf("google.") !== -1) return "google";
    return host.replace(/^www\./, "");
  }

  function readStoredAttribution() {
    try { return JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || "{}") || {}; }
    catch (e) { return {}; }
  }

  function writeStoredAttribution(data) {
    try { localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data)); } catch (e) {}
  }

  function captureFirstTouch() {
    var current = paramsFromSearch();
    var stored = readStoredAttribution();
    var referrerSource = sourceFromReferrer(document.referrer || "");

    if (stored.first_seen_at) return;

    if (Object.keys(current).length > 0) {
      current.first_seen_at = new Date().toISOString();
      current.landing_page = window.location.href;
      current.referring_site = document.referrer || "";
      writeStoredAttribution(current);
    } else if (referrerSource) {
      writeStoredAttribution({
        utm_source: referrerSource,
        utm_medium: "referral",
        first_seen_at: new Date().toISOString(),
        landing_page: window.location.href,
        referring_site: document.referrer || ""
      });
    }
  }

  function attributionForBeehiiv() {
    var current = paramsFromSearch();
    var stored = readStoredAttribution();
    var referrerSource = sourceFromReferrer(document.referrer || "");
    var merged = {};

    TRACKED_KEYS.forEach(function (key) {
      if (current[key]) merged[key] = current[key];
      else if (stored[key]) merged[key] = stored[key];
    });

    if (!merged.utm_source && referrerSource) merged.utm_source = referrerSource;
    if (!merged.utm_medium && referrerSource && merged.utm_source === referrerSource) {
      merged.utm_medium = "referral";
    }
    if (stored.referring_site || document.referrer) {
      merged.referring_site = stored.referring_site || document.referrer;
    }
    merged.pp_landing_page = stored.landing_page || window.location.href;
    merged.pp_current_page = window.location.href;
    return merged;
  }

  function writePendingLead(sourcePath) {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({
        source_path: sourcePath,
        created_at: Date.now()
      }));
    } catch (e) {}
  }

  function readPendingLead() {
    var pending = {};
    try { pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "{}") || {}; }
    catch (e) { return {}; }

    if (!LEAD_PATHS[pending.source_path] || !pending.created_at) return {};
    if (Date.now() - Number(pending.created_at) > PENDING_TTL_MS) {
      try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
      return {};
    }
    return pending;
  }

  function decorateBeehiivFrame(frame) {
    if (!frame || !frame.src) return;
    try {
      var url = new URL(frame.src);
      if (url.origin !== BEEHIIV_ORIGIN) return;
      if (LEAD_PATHS[currentPath]) writePendingLead(currentPath);

      var attrs = attributionForBeehiiv();
      Object.keys(attrs).forEach(function (key) {
        if (attrs[key] && !url.searchParams.get(key)) url.searchParams.set(key, attrs[key]);
      });
      var next = url.toString();
      if (frame.src !== next) frame.src = next;
    } catch (e) {}
  }

  function decorateBeehiivFrames() {
    document.querySelectorAll('iframe[src*="subscribe-forms.beehiiv.com"]').forEach(decorateBeehiivFrame);
  }

  function marketingAllowed() {
    try {
      if (window.Shopify && Shopify.customerPrivacy && typeof Shopify.customerPrivacy.marketingAllowed === "function") {
        return Shopify.customerPrivacy.marketingAllowed();
      }
    } catch (e) {}
    return true;
  }

  function leadAlreadyFired(sourcePath) {
    if (inMemoryLeadFired) return true;
    try { return sessionStorage.getItem("pp_meta_lead_fired_v2:" + sourcePath) === "1"; }
    catch (e) { return false; }
  }

  function markLeadFired(sourcePath) {
    inMemoryLeadFired = true;
    try { sessionStorage.setItem("pp_meta_lead_fired_v2:" + sourcePath, "1"); } catch (e) {}
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
  }

  function sendMetaLead(reason, sourcePath) {
    var content = {
      content_name: "Phantastic Physics Lead Magnet",
      content_category: "lead_magnet",
      source_path: sourcePath,
      confirmation_method: reason
    };

    if (typeof window.fbq === "function") {
      window.fbq("track", "Lead", content);
    } else {
      var params = {
        id: META_PIXEL_ID,
        ev: "Lead",
        dl: window.location.href,
        rl: document.referrer || "",
        if: "false",
        ts: Math.floor(Date.now() / 1000),
        "cd[content_name]": content.content_name,
        "cd[content_category]": content.content_category,
        "cd[source_path]": sourcePath,
        "cd[confirmation_method]": reason
      };
      var query = Object.keys(params).map(function (key) {
        return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
      }).join("&");
      var image = new Image();
      image.src = "https://www.facebook.com/tr/?" + query;
    }

    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish("pp_lead_magnet_submit", {
          path: sourcePath,
          reason: reason
        });
      }
    } catch (e) {}
  }

  function fireConfirmedLead(reason, sourcePath) {
    sourcePath = normalizePath(sourcePath);
    if (!LEAD_PATHS[sourcePath] || leadAlreadyFired(sourcePath)) return;

    confirmedLead = { reason: reason, source_path: sourcePath };
    if (!marketingAllowed()) return;

    markLeadFired(sourcePath);
    sendMetaLead(reason, sourcePath);
  }

  function successSourceFromReferrer() {
    try {
      var referrer = new URL(document.referrer || "");
      if (referrer.origin !== window.location.origin) return "";
      var referrerPath = normalizePath(referrer.pathname);
      return LEAD_PATHS[referrerPath] ? referrerPath : "";
    } catch (e) {
      return "";
    }
  }

  function fireForConfirmedSuccessRedirect() {
    if (currentPath !== SUCCESS_PATH) return;
    var pending = readPendingLead();
    var sourcePath = pending.source_path || successSourceFromReferrer();
    if (sourcePath) fireConfirmedLead("beehiiv_success_redirect", sourcePath);
  }

  function isKnownBeehiivFrameWindow(sourceWindow) {
    var frames = document.querySelectorAll('iframe[src*="subscribe-forms.beehiiv.com"]');
    for (var index = 0; index < frames.length; index += 1) {
      if (frames[index].contentWindow === sourceWindow) return true;
    }
    return false;
  }

  function normalizedMessageValue(value) {
    return String(value || "").toLowerCase().replace(/[\s-]+/g, "_");
  }

  function isConfirmedBeehiivSuccess(data) {
    var exactSuccessEvents = {
      "beehiiv:subscribe:success": true,
      "beehiiv:subscription:success": true,
      "beehiiv_subscribe_success": true,
      "subscribe_success": true,
      "subscription_success": true,
      "email_submitted": true
    };

    if (typeof data === "string") return Boolean(exactSuccessEvents[normalizedMessageValue(data)]);
    if (!data || typeof data !== "object") return false;

    var eventName = normalizedMessageValue(data.type || data.event || data.name || data.action);
    var status = normalizedMessageValue(data.status || data.result || data.state);
    if (exactSuccessEvents[eventName]) return true;
    return /^(subscribe|subscription|email_submission|form_submission)$/.test(eventName) &&
      /^(success|succeeded|subscribed|complete|completed)$/.test(status);
  }

  function handleBeehiivMessage(event) {
    if (!LEAD_PATHS[currentPath]) return;
    if (event.origin !== BEEHIIV_ORIGIN || !isKnownBeehiivFrameWindow(event.source)) return;
    if (isConfirmedBeehiivSuccess(event.data)) {
      fireConfirmedLead("beehiiv_success_message", currentPath);
    }
  }

  function bootLeadAttribution() {
    captureFirstTouch();
    if (ATTRIBUTION_PATHS[currentPath]) decorateBeehiivFrames();
    fireForConfirmedSuccessRedirect();
  }

  window.addEventListener("message", handleBeehiivMessage);
  document.addEventListener("visitorConsentCollected", function () {
    if (confirmedLead && marketingAllowed()) {
      fireConfirmedLead(confirmedLead.reason, confirmedLead.source_path);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootLeadAttribution);
  } else {
    bootLeadAttribution();
  }

  if (ATTRIBUTION_PATHS[currentPath]) {
    setTimeout(decorateBeehiivFrames, 800);
    setTimeout(decorateBeehiivFrames, 2500);
    try {
      new MutationObserver(decorateBeehiivFrames).observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }
})();
/* /PP_LEAD_ATTRIBUTION_V2 */
